import type { SavedCardGeometry } from "@/predictions/savedCardGeometry";
import { pixelIndicesToRuns } from "@/predictions/savedCardGeometry";
import type { PreviewFrameGeometry } from "@/utils/dicomPreviewFrame";

type Vector = number[];
export const dot = (a: Vector, b: Vector) => a.reduce((sum, v, i) => sum + v * b[i], 0);
export const cross = (a: Vector, b: Vector) => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];
const subtract = (a: Vector, b: Vector) => a.map((v, i) => v - b[i]);

/** Keep sparse storage; sampling does not allocate another full study volume. */
export function savedVolumeSampler(volume: NonNullable<SavedCardGeometry["volume"]>) {
  const { affine: a, rows, columns, runs } = volume;
  if (a.length !== 12 || !a.every(Number.isFinite) || !runs.length || rows <= 0 || columns <= 0)
    return null;
  const axes = [0, 1, 2].map((i) => [a[i], a[i + 4], a[i + 8]]);
  const origin = [a[3], a[7], a[11]];
  const determinant = dot(axes[0], cross(axes[1], axes[2]));
  if (Math.abs(determinant) < 1e-10) return null;
  const inverse = [cross(axes[1], axes[2]), cross(axes[2], axes[0]), cross(axes[0], axes[1])];
  const min = [Infinity, Infinity, Infinity];
  const max = [-Infinity, -Infinity, -Infinity];
  // Split runs at column boundaries to recover bounds without expanding every voxel.
  for (const [start, length] of runs) {
    for (let index = start; index < start + length;) {
      const end = Math.min(start + length, (Math.floor(index / rows) + 1) * rows);
      const column = Math.floor(index / rows) % columns;
      const slice = Math.floor(index / (rows * columns));
      const lo = [column, index % rows, slice];
      const hi = [column, (end - 1) % rows, slice];
      lo.forEach((v, i) => {
        min[i] = Math.min(min[i], v - 0.5);
        max[i] = Math.max(max[i], hi[i] + 0.5);
      });
      index = end;
    }
  }
  const corners = Array.from({ length: 8 }, (_, bits) =>
    origin.map(
      (v, axis) =>
        v +
        axes.reduce(
          (sum, direction, i) => sum + direction[axis] * (bits & (1 << i) ? max[i] : min[i]),
          0,
        ),
    ),
  );
  const contains = (world: Vector) => {
    const delta = subtract(world, origin);
    const [column, row, slice] = inverse.map((axis) => Math.round(dot(axis, delta) / determinant));
    if (column < 0 || column >= columns || row < 0 || row >= rows || slice < 0) return false;
    const index = row + rows * (column + columns * slice);
    let low = 0,
      high = runs.length - 1;
    while (low <= high) {
      const mid = (low + high) >>> 1;
      const [start, length] = runs[mid];
      if (index < start) high = mid - 1;
      else if (index >= start + length) low = mid + 1;
      else return true;
    }
    return false;
  };
  return { corners, contains };
}

/** Nearest-neighbour label sampling at real native CT pixel centres. */
export function sampleNativePlane(
  sampler: NonNullable<ReturnType<typeof savedVolumeSampler>>,
  frame: PreviewFrameGeometry,
) {
  const { rows, columns, image_position_patient: origin, image_orientation_patient: iop } = frame;
  const spacing = frame.pixel_spacing?.split("\\").map(Number);
  if (
    !rows ||
    !columns ||
    origin?.length !== 3 ||
    iop?.length !== 6 ||
    spacing?.length !== 2 ||
    ![...origin, ...iop, ...spacing].every(Number.isFinite) ||
    spacing.some((v) => v <= 0)
  )
    return null;
  const x = iop.slice(0, 3),
    y = iop.slice(3);
  const projected = sampler.corners.map((point) => {
    const delta = subtract(point, origin);
    return [dot(delta, x) / spacing[1], dot(delta, y) / spacing[0]];
  });
  const minColumn = Math.max(0, Math.floor(Math.min(...projected.map((p) => p[0]))));
  const maxColumn = Math.min(columns - 1, Math.ceil(Math.max(...projected.map((p) => p[0]))));
  const minRow = Math.max(0, Math.floor(Math.min(...projected.map((p) => p[1]))));
  const maxRow = Math.min(rows - 1, Math.ceil(Math.max(...projected.map((p) => p[1]))));
  const indices: number[] = [];
  for (let row = minRow; row <= maxRow; row++) {
    for (let column = minColumn; column <= maxColumn; column++) {
      const world = origin.map(
        (v, axis) => v + x[axis] * column * spacing[1] + y[axis] * row * spacing[0],
      );
      if (sampler.contains(world)) indices.push(row * columns + column);
    }
  }
  return {
    count: indices.length,
    runs: pixelIndicesToRuns(indices),
    affine: [0, 1, 2].flatMap((axis) => [
      x[axis] * spacing[1],
      y[axis] * spacing[0],
      0,
      origin[axis],
    ]),
  };
}
